import api from './api';
import type { OrdenCompra } from '../types/ordenes';

// Tipos para las respuestas de la API
interface ListarOrdenesResponse {
  data: OrdenCompra[];
  total: number;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface CreateOrdenRequest {
  numero_orden: string;
  proveedor_id: number;
  proveedor?: string; // Opcional para compatibilidad con código antiguo
  fecha_solicitud: string;
  fecha_entrega_esperada?: string;
  observaciones?: string;
  solicitado_por: number;
  detalles: {
    ingrediente_id: number;
    cantidad_solicitada: number;
    precio_unitario: number;
    observaciones?: string;
  }[];
}

interface ApiResponse<T> {
  message?: string;
  data: T;
}

// Servicios para órdenes de compra
export const ordenesCompraAPI = {
  // GET /api/ordenes-compra - Listar órdenes de compra
  listar: async (params?: {
    estado?: string;
    proveedor?: string;
    fecha_desde?: string;
    fecha_hasta?: string;
    page?: number;
    limit?: number;
  }): Promise<ListarOrdenesResponse> => {
    const response = await api.get('/ordenes-compra', { params });
    const backendData = response.data;

    // Mapear cada orden de snake_case a camelCase
    const ordenesMapeadas =
      backendData.data?.map((orden: any) => {
        const total = parseFloat(orden.total || '0');
        const subtotal = parseFloat(orden.subtotal || '0');

        // Debug: ver si hay algún valor extraño
        if (total > 1000000) {
          console.warn('⚠️ Total muy alto detectado:', {
            id: orden.id,
            numero: orden.numero_orden,
            total_raw: orden.total,
            total_parsed: total,
            subtotal_raw: orden.subtotal,
            subtotal_parsed: subtotal,
          });
        }

        return {
          id: orden.id?.toString() || '',
          numero: orden.numero_orden || '',
          fecha: orden.fecha_solicitud || '',
          estado: orden.estado || 'borrador',
          proveedor_nombre: orden.proveedor_nombre || '',
          proveedor: orden.proveedor_nombre || '', // Mantener compatibilidad
          total,
          subtotal,
          impuestos: parseFloat(orden.impuestos || '0'),
          observaciones: orden.observaciones,
          fechaEsperada: orden.fecha_entrega_esperada,
          fechaRecepcion: orden.fecha_recepcion,
          fechaCreacion: orden.created_at,
          fechaActualizacion: orden.updated_at,
          items: Array(parseInt(orden.total_items || '0')).fill({}), // Crear array con longitud = total_items
        };
      }) || [];

    return {
      data: ordenesMapeadas,
      total: backendData.total || ordenesMapeadas.length,
      pagination: backendData.pagination,
    };
  },

  // GET /api/ordenes-compra/:id - Obtener orden específica
  obtener: async (id: string): Promise<OrdenCompra> => {
    const response = await api.get(`/ordenes-compra/${id}`);
    const data = response.data.data; // El backend devuelve {success: true, data: {...}}

    // Mapear de snake_case a camelCase
    return {
      id: data.id.toString(),
      numero: data.numero_orden,
      fecha: data.fecha_solicitud,
      proveedor: data.proveedor_nombre || data.proveedor || 'Sin proveedor', // ✅ Fix: usar proveedor_nombre
      proveedor_id: data.proveedor_id, // ✅ Agregar proveedor_id
      fechaSolicitud: data.fecha_solicitud,
      fechaEsperada: data.fecha_entrega_esperada,
      fechaRecepcion: data.fecha_recepcion,
      estado: data.estado,
      total: parseFloat(data.total || '0'),
      subtotal: parseFloat(data.subtotal || '0'),
      impuestos: parseFloat(data.impuestos || '0'),
      observaciones: data.observaciones,
      tipo: data.tipo,
      ordenOriginalId: data.orden_original_id?.toString(),
      numeroOrdenOriginal: data.numero_orden_original,
      fechaCreacion: data.created_at,
      fechaActualizacion: data.updated_at,
      ordenesGeneradas:
        data.ordenes_generadas?.map((og: any) => ({
          id: og.id.toString(),
          numero: og.numero_orden,
          estado: og.estado,
          total: parseFloat(og.total || '0'),
          fechaCreacion: og.created_at,
        })) || [],
      usuarioCreador: data.usuario_nombre
        ? ({
            id: data.solicitado_por?.toString() || '0',
            nombre: data.usuario_nombre,
            email: '',
          } as any)
        : undefined,
      solicitante: data.usuario_nombre
        ? ({
            id: data.solicitado_por || 0,
            nombre: data.usuario_nombre,
            email: '',
          } as any)
        : undefined,
      items:
        data.detalles?.map((detalle: any) => ({
          id: detalle.id.toString(),
          ingredienteId: detalle.ingrediente_id.toString(),
          ingrediente: {
            id: detalle.ingrediente_id.toString(),
            nombre: detalle.ingrediente_nombre,
            unidadMedida: {
              abreviacion: detalle.ingrediente_unidad,
              nombre: detalle.ingrediente_unidad,
            },
          },
          unidad: detalle.ingrediente_unidad,
          cantidad: parseFloat(detalle.cantidad_solicitada || '0'),
          cantidadRecibida: parseFloat(detalle.cantidad_recibida || '0'),
          precioUnitario: parseFloat(detalle.precio_unitario || '0'),
          subtotal: parseFloat(detalle.subtotal || '0'),
          estadoItem: detalle.estado_item,
          observaciones: detalle.observaciones,
          historialRecepciones:
            detalle.historial_recepciones?.map((h: any) => ({
              id: h.id.toString(),
              cantidadRecibida: parseFloat(h.cantidad_recibida || '0'),
              fechaRecepcion: h.fecha_recepcion,
              observaciones: h.observaciones,
              usuario: {
                id: h.usuario_id?.toString() || '0',
                nombre: h.usuario_nombre || 'Desconocido',
                email: h.usuario_email || '',
              },
              fechaCreacion: h.created_at,
            })) || [],
        })) || [],
    } as OrdenCompra;
  },

  // POST /api/ordenes-compra - Crear nueva orden de compra
  crear: async (
    data: CreateOrdenRequest
  ): Promise<ApiResponse<{ id: string; numero: string; estado: string }>> => {
    const response = await api.post('/ordenes-compra', data);
    return response.data;
  },

  // PUT /api/ordenes-compra/:id - Actualizar orden completa
  actualizar: async (
    id: string,
    data: Partial<CreateOrdenRequest>
  ): Promise<ApiResponse<any>> => {
    const response = await api.put(`/ordenes-compra/${id}`, data);
    return response.data;
  },

  // PATCH /api/ordenes-compra/:id/enviar - Enviar orden a proveedor
  enviar: async (
    id: string
  ): Promise<ApiResponse<{ id: string; estado: string }>> => {
    const response = await api.patch(`/ordenes-compra/${id}/enviar`);
    return response.data;
  },

  // PATCH /api/ordenes-compra/:id/cancelar - Cancelar orden
  cancelar: async (
    id: string,
    motivo?: string
  ): Promise<ApiResponse<{ id: string; estado: string }>> => {
    const response = await api.patch(`/ordenes-compra/${id}/cancelar`, {
      motivo,
    });
    return response.data;
  },

  // POST /api/ordenes-compra/:id/recepcion - Recepcionar orden
  recepcionar: async (
    id: string,
    data: {
      fecha_recepcion: string;
      observaciones_recepcion?: string;
      items: {
        detalle_id: number;
        cantidad_recibida: number;
        observaciones?: string;
        // Opcional. Si no viene, el lote se crea sin vencimiento y, como el
        // consumo es FEFO, queda al final de la cola de salida.
        fecha_vencimiento?: string;
      }[];
    }
  ): Promise<ApiResponse<any>> => {
    const response = await api.post(`/ordenes-compra/${id}/recepcion`, data);
    return response.data;
  },

  // DELETE /api/ordenes-compra/:id - Eliminar orden (solo borradores)
  eliminar: async (id: string): Promise<ApiResponse<any>> => {
    const response = await api.delete(`/ordenes-compra/${id}`);
    return response.data;
  },

  // GET /api/ordenes-compra/sugerencias - Obtener sugerencias de órdenes
  obtenerSugerencias: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/ordenes-compra/sugerencias');
    return response.data;
  },

  // POST /api/ordenes-compra/desde-sugerencias - Crear órdenes desde sugerencias
  crearDesdesugerencias: async (datos: {
    sugerencias: {
      proveedor: string;
      fecha_entrega_esperada?: string;
      ingredientes: {
        ingrediente_id: number;
        cantidad_sugerida: number;
        costo_unitario: number;
        detalles_pedidos?: any[];
      }[];
    }[];
    observaciones_generales?: string;
    actualizar_precios?: boolean;
  }): Promise<
    ApiResponse<{
      resumen: {
        ordenes_creadas: number;
        total_items: number;
        total_estimado: number;
        precios_actualizados: number;
      };
      ordenes: OrdenCompra[];
    }>
  > => {
    const response = await api.post('/ordenes-compra/desde-sugerencias', datos);
    return response.data;
  },

  // Métodos de conveniencia para filtrar por estado
  listarActivas: async (): Promise<OrdenCompra[]> => {
    const response = await ordenesCompraAPI.listar({
      estado: 'pendiente,enviada,en_transito',
    });
    return response.data;
  },

  listarCompletadas: async (): Promise<OrdenCompra[]> => {
    const response = await ordenesCompraAPI.listar({
      estado: 'recibida',
    });
    return response.data;
  },

  // GET /api/ordenes-compra/ingredientes-proveedor/:proveedorId - Obtener ingredientes disponibles por proveedor
  obtenerIngredientesPorProveedor: async (
    proveedorId: number
  ): Promise<{
    success: boolean;
    data: any[];
    proveedor: {
      id: number;
      nombre: string;
      categoria: string;
    };
  }> => {
    const response = await api.get(
      `/ordenes-compra/ingredientes-proveedor/${proveedorId}`
    );
    return response.data;
  },
};

export default ordenesCompraAPI;
